import { alpha, SxProps, Theme } from "@mui/material";
import { COLOR_CONSTANTS } from "src/domains/shared/constants/styles";

/**
 * 레이어 패널 아이콘의 기본 크기 (2D·3D 공용).
 *
 * 섹션 헤더(셰브론)·검색·삭제 토글·그래프 진입·이름변경 확인, 그리고 3D 도움말
 * 툴팁이 이 값을 쓴다 — 아이콘마다 12·16·20 이 섞여 같은 행 안에서 크기가 튀던 것을
 * 모았다. 그 밖의 행 아이콘은 각자 토큰이 소유한다(보조 조작 `LAYER_SECONDARY_ICON_SIZE` ·
 * 색상 칩 `LAYER_COLOR_CHIP_SIZE`).
 * 토큰 밖 예외는 `NoLayersPlaceholder` 의 빈 상태 일러스트 하나뿐이다 — 행 아이콘이 아니라
 * `Icon.size` 리터럴로 낼 수 없는 크기(70×70)라 그 파일의 지역 sx 가 직접 소유한다.
 * 즉 이 패널의 아이콘 크기 정책은 위 토큰 3종 + 그 예외 하나로 끝난다.
 * `Icon.size` 는 12|16|20|24 리터럴만 받으므로 `as const` 로 리터럴 타입을 유지한다.
 */
export const LAYER_ICON_SIZE = 16 as const;

/**
 * 행의 보조 조작(secondary action) 아이콘 크기 — 이름변경 연필과 Fly-to(모델로 이동)가
 * 함께 쓴다. 주 조작보다 한 단계 작게 그린다.
 */
export const LAYER_SECONDARY_ICON_SIZE = 12 as const;

/** 색상 칩(원형 트리거) 한 변 — 채워진 원이라 같은 px 의 선 아이콘보다 커 보여 줄인다 */
export const LAYER_COLOR_CHIP_SIZE = 14;

/**
 * 아래 `SWITCH_STYLES` 스위치의 thumb 이동 거리(px) — 체크되면 thumb 가 이만큼 오른쪽으로 간다.
 * 그 스위치의 기하에서 나온 값이다: width 26 − switchBase padding 2×2 − thumb 10.
 * 같은 스위치 마크업을 그리는 폴더 표시 컨트롤(`FolderShowControl`)은 혼합 상태의 thumb 를 이 거리의 절반에
 * 세워 트랙 가운데에 둔다 — 스위치의 폭·thumb·padding 을 바꾸면 이 값도 함께 고친다.
 */
export const SWITCH_THUMB_TRAVEL_PX = 12;

/**
 * 레이어 패널 공통 Switch — 색상과 함께 크기도 이 토큰이 소유한다.
 * iOS 식 필(pill): 트랙이 26×14 전체를 채우고(root padding 0) 흰 thumb 10 이 트랙
 * **안쪽**에서 움직인다 — MUI 기본(가는 트랙 위로 thumb 가 튀어나오는 모양)과 다르다.
 * 기하 제약: thumb + switchBase padding×2 = height,
 * translateX(`SWITCH_THUMB_TRAVEL_PX`) = width − switchBase padding×2 − thumb.
 * 트랙이 색을 전담하므로 thumb 는 on/off 모두 흰색 고정 — 트랙 기본 opacity(0.38)를
 * 1 로 되돌리지 않으면 off 색이 회색 위 회색으로 뭉개진다.
 */
export const SWITCH_STYLES: SxProps<Theme> = {
  width: "26px",
  height: "14px",
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: "2px",
    color: "#ffffff",
    "&.Mui-checked": {
      transform: `translateX(${SWITCH_THUMB_TRAVEL_PX}px)`,
      color: "#ffffff",
    },
  },
  "& .MuiSwitch-thumb": {
    width: "10px",
    height: "10px",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
  },
  "& .MuiSwitch-track": {
    borderRadius: "999px",
    backgroundColor: "rgba(0, 0, 0, 0.22)",
    opacity: 1,
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: COLOR_CONSTANTS.primary.main,
    opacity: 1,
  },
  // root padding 0 이라 포커스 링이 밖으로 못 그려진다 — 트랙에 직접 그린다
  "& .MuiSwitch-switchBase.Mui-focusVisible + .MuiSwitch-track": {
    boxShadow: `0 0 0 2px ${alpha(COLOR_CONSTANTS.primary.main, 0.35)}`,
  },
};

/**
 * 레이어 행의 선두 컨트롤 자리(`SWITCH_CONTAINER_STYLES`)와 이름 사이 간격 — 잎행(`MENU_ITEM_STYLES`)과
 * 카드 행(`LayerCard` 의 `topLeft`)이 함께 읽는다. 2D 레이어 패널은 주 레이어 잎행 아래에 GIS·선형 카드 행을
 * 쌓으므로, 값이 갈리면 한 패널 안 두 섹션의 토글~이름 간격이 어긋난다.
 * 두 곳 모두 flex `gap` 이라 같은 컨테이너의 뒤 형제(이름 편집 확인 버튼 등) 사이에도 걸린다.
 * `MENU_ITEM_STYLES` 가 모듈 평가 중에 읽으므로 그보다 위에 둔다(아래 카드 행 절로 옮기면 TDZ).
 */
export const LAYER_ROW_TOGGLE_NAME_GAP = "8px";

/**
 * 눌리는 표면(잎행 MenuItem·섹션 헤더 행·셰브론·삭제 토글)의 hover 톤.
 * 같은 패널 안에서 무엇을 누를 수 있는지 알려주는 신호라 한 값이어야 한다 —
 * 톤이 갈리면 같은 목록 안에서 어떤 줄만 더 진하게 반응한다.
 * 아래 `LAYER_SURFACE_*` 와 같은 표면 토큰 무리지만, 첫 소비처(바로 아래 `MENU_ITEM_STYLES`)
 * 보다 먼저 평가돼야 해서 여기 선언한다.
 */
export const LAYER_HOVER_SURFACE = "rgba(0, 0, 0, 0.04)";

// 공통 MenuItem 스타일
export const MENU_ITEM_STYLES: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  padding: "2px 0px",
  minHeight: "32px",
  // 토글 자리와 이름 사이 간격 — 카드 행(LayerCard 의 topLeft)과 같은 토큰을 읽는다
  gap: LAYER_ROW_TOGGLE_NAME_GAP,
  borderRadius: "4px",
  paddingRight: "4px",
  "&:hover": {
    backgroundColor: LAYER_HOVER_SURFACE,
  },
};

// Switch 컨테이너 Box 스타일
export const SWITCH_CONTAINER_STYLES: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  width: "40px",
  justifyContent: "center",
};

// Layer 이름 컨테이너 스타일
export const LAYER_NAME_CONTAINER_STYLES: SxProps<Theme> = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  overflow: "hidden",
  marginRight: "8px",
};

/**
 * 레이어 이름의 타이포 — 표시 텍스트와 인라인 편집 input 이 공유한다.
 * 두 스타일은 `LayerMenuItem` 의 `renderLayerName` 에서 **같은 슬롯을 제자리 교체**하므로,
 * 이 값이 갈라지면 편집 진입 순간 글자가 튄다. 그래서 한 곳에서만 적는다.
 */
const LAYER_NAME_TYPOGRAPHY = {
  fontFamily: "Pretendard, sans-serif",
  fontWeight: 500,
  fontSize: "14px",
  letterSpacing: "0.4px",
  lineHeight: "24px",
  color: "rgba(0, 0, 0, 0.87)",
};

// Layer 이름 텍스트 스타일
export const LAYER_NAME_TEXT_STYLES: SxProps<Theme> = {
  ...LAYER_NAME_TYPOGRAPHY,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

// 인라인 이름 편집 input 스타일 (이름 텍스트와 같은 타이포, 테두리 최소)
export const INLINE_RENAME_INPUT_STYLES: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  ...LAYER_NAME_TYPOGRAPHY,
  border: `1px solid ${COLOR_CONSTANTS.primary.main}`,
  borderRadius: "4px",
  padding: "0 4px",
  outline: "none",
  backgroundColor: "#ffffff",
};

/**
 * 잎행(`LayerMenuItem`)의 이름 편집 연필 **전용** — 이 토큰의 소비처는 그곳 하나다.
 * 카드 행(`LayerCard`)의 연필은 아래 `layerRowSecondaryActionButton` 쪽이 소유한다.
 * 상자 크기(16px)는 두 토큰이 같지만 이쪽엔 `borderRadius` 가 없어 합치면 잎행 연필의
 * 모서리가 바뀐다 — 시각 변경이라 통합하지 않고 소유 경계만 나눠 둔다.
 */
export const RENAME_PENCIL_BUTTON_STYLES: SxProps<Theme> = {
  width: 16,
  height: 16,
  padding: 0,
  color: COLOR_CONSTANTS.text.secondary,
};

// 행 우측 액션 묶음 (연필 + 그래프·색상) — 카드 행과 같은 간격
export const RIGHT_ACTIONS_STYLES: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: "4px",
};

// ─── 목록 표면 토큰 (2D·3D 공용) ──────────────────────────────────────────────
// 아코디언 안에 놓이는 것들 — 카드 행·검색 필드·삭제 토글 — 이 공유하는 표면 값.
// 섹션 카드(`sectionCard`)는 이들을 담는 바깥 면이라 한 단계 큰 라운드(`SECTION_CARD_RADIUS`)를
// 따로 쓴다.

export const LAYER_SURFACE_BORDER = "1px solid rgba(0, 0, 0, 0.1)";
export const LAYER_SURFACE_RADIUS = "8px";
/** 검색 필드·삭제 토글처럼 목록 위 한 줄에 나란히 서는 컨트롤의 높이 */
export const LAYER_CONTROL_SIZE = "28px";

// ─── 레이어 패널 본문 표면 (2D·3D 공용) ───────────────────────────────────────
// 2D `Inspection2dLayerPanel` 본문과 3D `layerPanelStyles.panelContent` 가 같은 바닥면 위에
// 같은 리듬으로 카드를 쌓는다. 값이 갈리면 두 검사 화면의 레이어 패널이 서로 다른 배경·간격이 된다.

/** 카드가 놓이는 바닥면 — 패널 본문, 그리고 본문 안에서 카드를 담는 폴더 트레이(`FolderTree` card 변형)가 쓴다. */
export const LAYER_PANEL_SURFACE_BG = "#f2f4f6";

/**
 * 본문의 카드 사이 간격이자 바깥 여백 — 카드가 같은 격자 위에 놓인다.
 * 이 간격을 음수 마진으로 상쇄하는 슬롯(2D 고도 색상 범례 자리)이 같은 값을 되돌려주므로,
 * 값이 갈라지면 접힌 범례 자리에 빈 틈이 남거나 카드가 붙는다.
 */
export const LAYER_PANEL_CARD_GAP = "12px";

/** 카드 목록이 놓이는 패널 본문의 공용 표면 — 스크롤 처리는 소비처가 위에 얹는다. */
export const layerPanelSurface = {
  display: "flex",
  flexDirection: "column",
  gap: LAYER_PANEL_CARD_GAP,
  padding: LAYER_PANEL_CARD_GAP,
  backgroundColor: LAYER_PANEL_SURFACE_BG,
} as const;

// ─── 카드형 레이어 행 (2D·3D 공용) ────────────────────────────────────────────
// 원래 3D `shared/components/map3d/layerPanelStyles.ts` 소유였으나 GIS 목록이 같은 카드 행을
// 쓰게 되며 이 파일로 승격했다. 카드 면(`modelCard`·`modelCardSelected`)은 공용 `LayerCard` 가
// 읽고 3D 는 그 카드를 렌더한다. 3D `layerPanelStyles.ts` 는 이 중 행 액션 버튼
// (`layerRowActionButton`·`layerRowSecondaryActionButton`)을 같은 이름으로 re-export 한다.

/**
 * 카드 행 선두 컨트롤 자리의 높이(px) — 교체되는 선두 컨트롤(스위치·스피너·체크박스) 중 가장 높은 체크박스(~24)
 * 기준이다. `LayerCard` 의 선두 자리가 이 높이로 서고, 폴더 트레이 머리 줄(`FolderRow` 의 `trayHeader`·
 * `trayHeaderNested`)이 아래 카드 여백과 함께 이 값에서 높이를 파생한다 — 접힌 트레이가 옆의 한 줄 카드와 같은
 * 높이로 선다.
 */
export const LAYER_CARD_LEADING_SLOT_PX = 24;

/**
 * 루트 카드(`modelCard`)의 안쪽 여백(px) — 폴더 트레이 머리 줄(`FolderRow` 의 `trayHeader`)이 높이(세로 여백×2 +
 * 선두 컨트롤 자리)와 오른쪽 여백(가로 여백)을 이 값에서 파생한다.
 */
export const LAYER_CARD_PADDING_PX = { y: 8, x: 10 } as const;

/**
 * 폴더 안 카드(`modelCardCompact`)의 안쪽 여백(px) — 안쪽 폴더 머리 줄(`FolderRow` 의 `trayHeaderNested`)이 같은
 * 식으로 높이와 오른쪽 여백을 이 값에서 파생한다.
 */
export const LAYER_CARD_COMPACT_PADDING_PX = { y: 4, x: 8 } as const;

export const modelCard = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  padding: `${LAYER_CARD_PADDING_PX.y}px ${LAYER_CARD_PADDING_PX.x}px`,
  borderRadius: LAYER_SURFACE_RADIUS,
  border: LAYER_SURFACE_BORDER,
  backgroundColor: "#ffffff",
  boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.08)",
  transition:
    "opacity 0.2s ease, border-color 0.12s ease, box-shadow 0.12s ease",
} as const;

/**
 * 폴더 안의 카드 행(`LayerCardDensity` compact) — 루트 카드보다 한 단계 작고 납작하게 선다. 세로 여백을 줄이고
 * 그림자를 뺀다(트레이 바닥면 위에서는 테두리로 충분하다). 선두 컨트롤 자리(`LAYER_CARD_LEADING_SLOT_PX`)는 그대로라
 * 편집 체크박스로 바뀌어도 높이가 흔들리지 않고, 한 줄 카드는 선두 컨트롤 자리 + 세로 여백
 * (`LAYER_CARD_COMPACT_PADDING_PX.y`)×2 + 테두리 2 다 — `FolderRow` 의 안쪽 폴더 머리 줄(`trayHeaderNested`)이 같은
 * 토큰에서 높이를 파생하고 테두리는 그 트레이가 둘러, 접힌 안쪽 폴더가 이 카드와 같은 높이로 선다.
 */
export const modelCardCompact = {
  gap: "4px",
  padding: `${LAYER_CARD_COMPACT_PADDING_PX.y}px ${LAYER_CARD_COMPACT_PADDING_PX.x}px`,
  boxShadow: "none",
} as const;

export const modelCardSelected = {
  borderColor: COLOR_CONSTANTS.primary.main,
  backgroundColor: alpha(COLOR_CONSTANTS.primary.main, 0.04),
  boxShadow: `0px 2px 6px ${alpha(COLOR_CONSTANTS.primary.main, 0.15)}`,
} as const;

/**
 * 인라인 이름 편집의 확인 버튼 — 카드 행·잎행이 같은 모양을 쓴다.
 * 누를 때 input 의 blur 가 먼저 터지지 않도록 소비처가 `onMouseDown` 을 막아야 한다.
 */
export const renameConfirmButton = {
  flexShrink: 0,
  width: 24,
  height: 24,
  padding: 0,
  color: COLOR_CONSTANTS.primary.main,
} as const;

/** 레이어 행 우측 액션 아이콘 버튼 (그래프 등 LAYER_ICON_SIZE 아이콘용) */
export const layerRowActionButton = {
  width: "20px",
  height: "20px",
  padding: 0,
  borderRadius: "4px",
  color: COLOR_CONSTANTS.text.secondary,
} as const;

/**
 * 행의 보조 조작(secondary action) 12px 아이콘 전용 컴팩트 버튼 — 카드 행(`LayerCard`)의
 * 이름변경 연필과 3D Fly-to(`FlyToLayerButton` · `MeshController`)가 함께 쓴다.
 * 잎행(`LayerMenuItem`)의 연필은 여기가 아니라 위 `RENAME_PENCIL_BUTTON_STYLES` 소유다.
 * 20px 상자에 12px 아이콘을 담으면 빈 여백이 옆 컨트롤과의 시각 간격을 벌린다.
 */
export const layerRowSecondaryActionButton = {
  ...layerRowActionButton,
  width: "16px",
  height: "16px",
} as const;

// ─── 삭제(편집) 모드 surface (2D·3D 공용) ─────────────────────────────────────
// 원래 3D `Inspection3d/Sidebar/vectorFieldStyles.ts` 소유였으나 GIS 목록의 삭제 토글이
// 같은 톤을 쓰게 되며 이 파일로 승격했다. 3D Vector 패널은 같은 이름으로 re-export 해 소비한다.
export const DELETE_MODE_SURFACE = "#fdecea";
export const DELETE_MODE_SURFACE_HOVER = "#fbdcd8";

// ─── 레이어 패널 섹션 카드 (2D·3D 공용) ───────────────────────────────────────
// 투명도 슬라이더 sx 와 같은 경위로 승격했다 — 2D 레이어 패널의 아코디언이 3D 와 같은
// 카드 시각 언어를 쓰게 되며 정의를 이 파일로 옮겼고, 3D `layerPanelStyles.ts` 는
// `sectionCard`·`sectionLabel` 을 같은 이름으로 re-export 한다.

/**
 * `LayerAccordion` 펼침 영역 바로 안의 세로 목록 래퍼 — 펼침 영역이 가로 flex 라
 * 세로 배치·전체 폭은 소비처가 이 래퍼로 만든다. 간격은 여기서 주지 않는다 —
 * 목록 간격의 소유는 `LayerSearchEditableList`(LIST_GAP)에 있고, 자체 목록을 직접
 * 나열하는 소비처(worksite 등)만 이 위에 gap 을 합성한다.
 */
export const accordionInnerColumn = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
} as const;

/**
 * 섹션 카드(`sectionCard`)의 바깥 반경 — 카드 안 면들의 `LAYER_SURFACE_RADIUS` 보다 한 단계
 * 크다. `LayerAccordion` 헤더 행이 hover 면의 모서리로 같은 값을 읽는다(펼치면 위 두 모서리만).
 * 카드에 `overflow: hidden` 이 없어 헤더의 hover 면을 카드가 잘라 주지 않으므로, 두 반경이
 * 갈라지면 hover 면의 모서리가 카드 곡선과 어긋난다.
 */
export const SECTION_CARD_RADIUS = "10px";

// sx 배열(`sx={[sectionCard, { ...지역 오버라이드 }]}`)로 합성 가능해야 하므로
// SxProps<Theme>(배열 변형 포함) 대신 단일 스타일 객체로 둔다.
export const sectionCard = {
  backgroundColor: "#ffffff",
  borderRadius: SECTION_CARD_RADIUS,
  border: "1px solid rgba(0, 0, 0, 0.08)",
} as const;

export const sectionLabel = {
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: "0.03em",
  color: "rgba(0, 0, 0, 0.5)",
} as const;

// ─── 단일 토글 카드 (2D·3D 공용) ──────────────────────────────────────────────
// 원래 3D `shared/components/map3d/layerPanelStyles.ts` 소유였으나 2D 어노테이션 토글이 같은
// 카드를 쓰게 되며 이 파일로 승격했다. 토글 카드 sx(`toggleRowCard`·`toggleRow`·
// `toggleRowNotAllowed`·`rowNameText`)는 공용 `LayerToggleCard` 가 읽고 3D 는 그 카드를
// 렌더한다 — 3D `layerPanelStyles.ts` 는 이 sx 를 re-export 하지 않는다.

/**
 * 카드 행·주 레이어 행의 레이어명 타이포.
 *
 * 표시 텍스트(`rowNameText`·`LayerCard.nameText`)와 인라인 편집 input
 * (`LayerCard.renameInput`), 그리고 잎행에 이 크기를 주입하는 2D 주 레이어 행이 함께 쓴다 —
 * 표시와 편집이 같은 자리를 제자리 교체하므로 값이 갈리면 편집 진입 순간 글자가 튄다.
 * (잎행 기본 타이포는 별개다 — 위 `LAYER_NAME_TYPOGRAPHY` 가 소유한다.)
 */
export const LAYER_CARD_NAME_TYPOGRAPHY = {
  fontSize: "12px",
  fontWeight: 500,
} as const;

export const rowNameText: SxProps<Theme> = {
  ...LAYER_CARD_NAME_TYPOGRAPHY,
};

/** 단일 토글 스위치 한 줄을 감싸는 섹션 카드 (어노테이션 · DSM 등) */
export const toggleRowCard = {
  ...sectionCard,
  width: "100%",
  padding: "4px 12px",
} as const;

/** 토글 스위치 + 라벨 한 줄 */
export const toggleRow = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  padding: "6px 0",
  cursor: "pointer",
  transition: "opacity 0.2s ease",
} as const;

/** 토글 자체가 비활성인 상태의 커서 처리 */
export const toggleRowNotAllowed = {
  cursor: "default",
} as const;


// ─── 레이어 투명도 슬라이더 (2D·3D 공용) ─────────────────────────────────────
// 원래 3D `shared/components/map3d/layerPanelStyles.ts` 소유였으나 2D tif 행 슬라이더가
// 같은 시각 언어를 쓰게 되며 이 파일로 승격했다 — 정의를 shared 에 두는 것은 shared 가 3D
// 도메인 파일을 역방향 import 하지 않기 위함이다. 이 sx 는 공용 `LayerTransparencySliderRow`
// 가 읽고 3D 는 그 행을 렌더한다 — 3D `layerPanelStyles.ts` 는 이 sx 를 re-export 하지 않는다.

const opacitySliderThumb = {
  width: 12,
  height: 12,
  boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.25)",
  "&:hover, &.Mui-focusVisible": {
    boxShadow: `0px 0px 0px 5px ${alpha(COLOR_CONSTANTS.primary.main, 0.16)}`,
  },
};

/**
 * 불투명도 슬라이더의 상태 무관 공통 레이아웃·색상. 두 변형이 공유한다.
 * 두 변형의 차이는 트랜지션의 on/off 가 아니라 지속시간이다.
 * - `opacitySliderSettling`: thumb `left` · track `width` 트랜지션을 0.3s 로 늘려,
 *   포인터 조작 없이 일어나는 값 점프를 부드럽게 잇는다.
 * - `opacitySlider`: 트랜지션을 선언하지 않아 MUI 기본값
 *   (`theme.transitions.duration.shortest`, MUI 기본 150ms) 을 그대로 둔다.
 *
 * 포인터 드래그 중 트랜지션 제거는 두 변형 모두 MUI 자체의 `.MuiSlider-dragging` 규칙이 하며
 * (sx 로 주입되는 규칙보다 특이도가 높아 위 선언을 덮는다) 이 모듈 소관이 아니다.
 *
 * 어느 변형을 쓸지는 소비처가 고르며, 이 모듈은 그 선택 시점을 알지도, 강제하지도 않는다.
 * - 자기 `settling` 상태로 두 변형을 오가는 소비처: 3D `LayerOpacitySlider` ·
 *   `useMeshOptions` → `MeshController` — 포인터 조작 없이 값이 점프하는 경로가 있다.
 * - `opacitySlider` 만 무조건 쓰는 소비처: 2D `LayerTransparencySliderRow` — 그 값 점프
 *   경로가 없어 `settling` 상태 자체를 두지 않는다.
 */
const opacitySliderBase = {
  flex: 1,
  // 원색(1.0)은 무겁고 0.55 는 너무 흐리다는 QA 피드백의 중간값
  color: alpha(COLOR_CONSTANTS.primary.main, 0.75),
  padding: "6px 0",
  marginLeft: "4px",
  "& .MuiSlider-rail": { opacity: 0.24 },
};

export const opacitySlider: SxProps<Theme> = {
  ...opacitySliderBase,
  "& .MuiSlider-thumb": opacitySliderThumb,
};

export const opacitySliderSettling: SxProps<Theme> = {
  ...opacitySliderBase,
  "& .MuiSlider-thumb": {
    ...opacitySliderThumb,
    transition: "left 0.3s ease, box-shadow 0.15s ease",
  },
  "& .MuiSlider-track": { transition: "width 0.3s ease" },
};

export const opacityRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  width: "100%",
  height: "30px",
  padding: "0 10px",
  borderRadius: "7px",
  backgroundColor: "#f6f7f9",
};

/**
 * 조작할 수 없는 투명도 행 — 슬라이더는 자기 `disabled` 톤을 갖지만 아이콘·라벨·값은
 * 그대로라 행의 절반만 꺼진 것처럼 보인다. 행 전체를 한 컨트롤로 읽히게 함께 낮춘다.
 * (카드 전체를 흐리게 하지 않는 대신, 실제로 막힌 이 행만 흐려진다.)
 */
export const opacityRowDisabled: SxProps<Theme> = {
  opacity: 0.45,
};

export const opacityLabel: SxProps<Theme> = {
  fontSize: "10px",
  fontWeight: 500,
  lineHeight: 1,
  color: "rgba(0, 0, 0, 0.6)",
  whiteSpace: "nowrap",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
};

export const opacityValueText: SxProps<Theme> = {
  fontSize: "10px",
  fontWeight: 600,
  lineHeight: 1,
  color: "rgba(0, 0, 0, 0.7)",
  width: "30px",
  textAlign: "right",
  flexShrink: 0,
  marginLeft: "-3px",
};
